import type { Result } from "./types/types";

export default class EventLogger {
  constructor() {
    this.setupEventListeners();
  }

  truncateText(text: string, maxLength: number) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  }

  debounce<T extends (...args: any[]) => void>(func: T, delay: number) {
    let timer: ReturnType<typeof setTimeout>;

    return (...args: Parameters<T>) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  setupEventListeners() {
    document.addEventListener("contextmenu", this.handleContextMenu.bind(this));
    document.addEventListener("dblclick", this.handleDoubleClick.bind(this));
    document.addEventListener("click", this.handleClick.bind(this));
    document.addEventListener("mouseover", this.handleMouseOver.bind(this));
    //document.addEventListener("mouseout", this.handleMouseOut.bind(this));
  }

  handleContextMenu(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    let elementType = "";
    let elementInfo = "";
    const result: Result = {};

    // Determine the event name based on the element right-clicked
    if (target instanceof HTMLButtonElement) {
      elementType = "button";
      elementInfo = this.truncateText(target.innerText.trim(), 50);
    } else if (target instanceof HTMLInputElement) {
      switch (target.type.toLowerCase()) {
        case "checkbox":
          elementType = "checkbox";
          elementInfo = this.truncateText(
            (target.parentElement?.innerText ?? "").trim(),
            50
          );
          result["klaritics_selected"] = target.checked
            ? "Selected"
            : "Not Selected";
          result["klaritics_description"] = target?.value;
          break;
        case "radio":
          elementType = "radio";
          elementInfo = this.truncateText(
            (target.parentElement?.innerText ?? "").trim(),
            50
          );
          result["klaritics_description"] = target.value;
          break;
        case "text":
          elementType = "text_input";
          elementInfo = this.truncateText(target.innerText?.trim(), 50);
          break;
        case "button":
          elementType = "button";
          elementInfo = this.truncateText(target.innerText?.trim(), 50);
          break;
      }
    } else {
      elementType = "other";
      elementInfo = this.truncateText(target.innerText?.trim(), 50)
        ? this.truncateText(target.innerText?.trim(), 50)
        : "";
    }

    result["klaritics_trigger"] = "right_click";
    result["klaritics_value"] = elementInfo;
    result["klaritics_view_type"] = elementType;

    window.Klaritics.logEvent("$clicked", result);
  }

  handleDoubleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    let elementType = "";
    let elementInfo = "";
    const result: Result = {};

    // Determine the event name based on the element double-clicked
    if (target instanceof HTMLButtonElement) {
      elementType = "button";
      elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
    } else if (target instanceof HTMLInputElement) {
      switch (target.type.toLowerCase()) {
        case "text":
          elementType = "text_input";
          elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
          break;
        case "button":
          elementType = "button";
          elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
          break;
      }
    } else {
      elementType = "other";
      elementInfo = this.truncateText(target.innerText?.trim(), 50)
        ? this.truncateText(target.innerText?.trim(), 50)
        : ""; // Limit to 50 characters
    }

    result["klaritics_trigger"] = "double_click";
    result["klaritics_value"] = elementInfo;
    result["klaritics_view_type"] = elementType;

    window.Klaritics.logEvent("$clicked", result);
  }

  handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    let elementType = "";
    let elementInfo = "";
    const result: Result = {};

    // Determine the event name based on the element clicked
    if (target instanceof HTMLButtonElement) {
      elementType = "button";
      elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
    } else if (target instanceof HTMLInputElement) {
      switch (target.type.toLowerCase()) {
        case "checkbox":
          elementType = "checkbox";
          elementInfo = this.truncateText(
            (target.parentElement?.innerText ?? "").trim(),
            50
          ); // Include label text
          result["klaritics_selected"] = target.checked
            ? "Selected"
            : "Not Selected"; // Selection state;
          result["klaritics_description"] = target?.value;
          break;
        case "radio":
          elementType = "radio";
          elementInfo = this.truncateText(
            (target.parentElement?.innerText ?? "").trim(),
            50
          ); // Include label text
          result["klaritics_description"] = target.value; // Selected value
          break;
        case "text":
          elementType = "text_input";
          elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
          break;
        case "button":
          elementType = "button";
          elementInfo = this.truncateText(target.innerText?.trim(), 50); // Limit to 50 characters
          break;
      }
    } else if (target instanceof HTMLAnchorElement) {
      elementType = "link";
      elementInfo = target.href; // Link source
      result["klaritics_url"] = target.target;
    } else {
      elementType = "other";
      elementInfo = this.truncateText(target.innerText?.trim(), 50)
        ? this.truncateText(target.innerText?.trim(), 50)
        : "";
    }

    result["klaritics_trigger"] = "single_click";
    result["klaritics_value"] = elementInfo;
    result["klaritics_view_type"] = elementType;

    window.Klaritics.logEvent("$clicked", result);
  }

  handleMouseOver(event: MouseEvent) {
    this.hoverTextInput(event);
  }

  hoverTextInput = this.debounce((event: MouseEvent) => {
    // Changed to arrow function
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    let elementType = "";
    let elementInfo = "";

    if (
      target instanceof HTMLInputElement &&
      target.type.toLowerCase() === "text"
    ) {
      elementType = "text_input";
      elementInfo = this.truncateText(target.value.trim(), 50);

      if (elementType && elementInfo) {
        const result = {
          klaritics_value: elementInfo,
          klaritics_view_type: elementType
        };
        window.Klaritics.logEvent("$hover", result);
      } else if (elementType) {
        window.Klaritics.logEvent("$hover");
      }
    } else if (target instanceof HTMLButtonElement) {
      elementType = "button";
      elementInfo = this.truncateText(target.innerText.trim(), 50);
      if (elementType && elementInfo) {
        const result = {
          klaritics_value: elementInfo,
          klaritics_view_type: elementType
        };
        window.Klaritics.logEvent("$hover", result);
      } else if (elementType) {
        window.Klaritics.logEvent("$hover");
      }
    }
  }, 1000);
}
